/* This will include:
    - A sidebar listing all the roots accessible to this user (ro access doesn't make a lot of sense, so rw)
    - A panel with the file tree of the selected root, consisting in turn of:
    - Breadcrumbs? Probably don't need them, easier to operate on the entire tree
    - For each folder, a foldout/accordion, expanded by default, recursively
    - For text and binary files, a generic icon and size
    - For images, a preview icon of the actual image (probably need some cache for that, maybe by file hash),
      image dimensions, some validation checkmark
    - In the navbar, an upload button? Or an upload button in every folder? The latter probably makes more sense
    - A "New folder" button
    - If the upload button is in each folder, then we'll need a root folder as well (which is okay, I guess)
    - Drag'n'drop for uploads and file juggling (dnd-kit?)
    - When a file is uploaded or moved, we need a thing that rolls out from the bottom with previews and confirm/cancel
    - Checkmarks for mass operation?
    - Action buttons for selection - if we have these, maybe we don't even need dnd? At least at first
    - No pagination
    - If there's a "Move" action button, then we'll need a destination folder selection dialog, and if we have that, we can do with a single upload button as well
    - Can we move stuff without that dialog? A separate mode to select a destination folder? May be easier, but needs obvious visuals and a CTA
 */

import {cn} from '@/lib/utils'
import {
  Loader2Icon,
  CircleAlertIcon,
  LayoutListIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FileBracesIcon,
  ImageIcon,
  FileMusicIcon,
  FilePlayIcon,
  FileIcon,
  type LucideProps,
  UploadIcon,
  XIcon,
} from 'lucide-react'
import {Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from '@/components/ui/collapsible'
import {Card, CardContent} from '@/components/ui/card'
import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert'
import {Empty, EmptyHeader, EmptyMedia, EmptyTitle} from '@/components/ui/empty'
import {NavLinks} from '@/components/nav-links'
import {useAuth} from '@/hooks/auth-provider'
import {useLocalStorage} from '@/hooks/use-local-storage'
import useSWR, {type Fetcher} from 'swr'
import type {FileData} from '@/types'
import {filesize} from 'filesize'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type RefAttributes,
  type SubmitEventHandler,
} from 'react'
import {Button} from './ui/button'
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemMetadata,
  FileUploadItemPreview,
  FileUploadItemProgress,
  FileUploadList,
  FileUploadTrigger,
  type FileUploadProps,
} from './ui/file-upload'

dayjs.extend(relativeTime)

type FSNode = {
  name: string
  path: string
  file?: FileData
  children: FSNode[]
}

const RootContext = createContext<string | null>(null)

function buildTree(files: FileData[], newDirs: string[]): FSNode {
  const root: FSNode = {name: '', path: '', children: []}

  function lookup(node: FSNode, name: string): FSNode {
    for (const child of node.children) if (child.name === name) return child

    const path = node.path ? `${node.path}/${name}` : name
    const newChild = {name, path, children: []}
    node.children.push(newChild)
    return newChild
  }

  for (const file of files) {
    const parts = file.path.split('/')
    let node = root
    while (parts.length > 1) {
      const [name] = parts.splice(0, 1)
      node = lookup(node, name!)
    }
    node.children.push({
      name: parts[0]!,
      path: file.path,
      file,
      children: [],
    })
  }
  for (const dir of newDirs) {
    const parts = dir.split('/')
    let node = root
    while (parts.length > 1) {
      const [name] = parts.splice(0, 1)
      node = lookup(node, name!)
    }
    node.children.push({
      name: parts[0]!,
      path: dir,
      children: [],
    })
  }
  return root
}

function FileTypeIcon({ext, ...props}: {ext: string} & Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>) {
  switch (ext.toLowerCase()) {
    case 'json':
      return <FileBracesIcon {...props} />
    case 'jpg':
    case 'jpeg':
    case 'png':
      return <ImageIcon {...props} />
    case 'mp3':
    case 'ogg':
      return <FileMusicIcon {...props} />
    case 'mp4':
      return <FilePlayIcon {...props} />
    default:
      return <FileIcon {...props} />
  }
}

function Node({className, node, ...props}: {node: FSNode} & React.ComponentProps<'div'>) {
  if (!node.file) return <Dir className={className} node={node} {...props} />

  return (
    <div className={cn('flex flex-row items-center justify-between p-2', className)} {...props}>
      <div className='flex flex-row items-center gap-2 grow-2'>
        <FileTypeIcon className='text-muted-foreground w-5' ext={node.name.replace(/^.+\./, '')} />
        <div title={node.path}>{node.name}</div>
      </div>
      {/* <div className='text-muted-foreground opacity-50'>•</div> */}
      <div className='w-30 text-right'>{filesize(node.file.size, {standard: 'iec'})}</div>
      <div className='w-40 text-right text-muted-foreground'>{dayjs(node.file.mtime).fromNow()}</div>
    </div>
  )
}

function Dir({node, ...props}: {node: FSNode} & React.ComponentProps<'div'>) {
  // The nature of .list.json guarantees there are no empty dirs
  const [open, setOpen] = useState(!node.path)
  const [isUploading, setIsUploading] = useState(false)

  function onUploadButtonClick(e: React.MouseEvent) {
    e.preventDefault()
    setIsUploading(true)
    setOpen(true)
  }

  function onXClick(e: React.MouseEvent) {
    e.preventDefault()
    setIsUploading(false)
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} {...props}>
      <CollapsibleTrigger className='cursor-pointer flex flex-row items-center gap-2 p-2'>
        {open ? <ChevronUpIcon className='text-muted-foreground w-5 h-4' /> : <ChevronDownIcon className='text-muted-foreground w-5 h-4' />}
        <div title={node.path}>{node.name}/</div>
        <div className='text-muted-foreground opacity-50'>•</div>
        <div className='text-muted-foreground'>{node.children.length} entries</div>
        <div className='text-muted-foreground opacity-50'>•</div>
        {!isUploading && <UploadIcon className='text-muted-foreground w-5 h-5' onClick={onUploadButtonClick} />}
        {isUploading && <XIcon className='text-muted-foreground w-5 h-5' onClick={onXClick} />}
      </CollapsibleTrigger>
      <CollapsibleContent className='ms-7'>
        {isUploading && <Uploader targetDir={node.path} />}

        {node.children.map(c => (
          <Node key={c.name} node={c} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

function Uploader({targetDir, ...props}: {targetDir: string} & ComponentProps<'div'>) {
  const rootContext = useContext(RootContext)
  const [files, setFiles] = useState<File[]>([])

  const auth = useAuth()

  const onUpload: NonNullable<FileUploadProps['onUpload']> = useCallback(async (files, {onProgress, onSuccess, onError}) => {
    try {
      console.log('Uploading', files)

      // Process each file individually
      const uploadPromises = files.map(async file => {
        try {
          // TODO: Stream

          const formData = new FormData()
          formData.append('file', file)

          const cleanFilename = file.name.replace(/[^a-z0-9_.-]/gi, '')

          onProgress(file, 10)

          await fetch(`/api/files/${rootContext}/${targetDir}/${cleanFilename}`, {
            method: 'POST',
            headers: auth.header,
            body: formData,
          })

          onProgress(file, 100)
          onSuccess(file)
        } catch (error) {
          console.error(error)
          onError(file, error instanceof Error ? error : new Error('Upload failed'))
        }
      })

      await Promise.all(uploadPromises)
    } catch (error) {
      // This handles any error that might occur outside the individual upload processes
      console.error('Unexpected error during upload:', error)
    }
  }, [])

  return (
    <FileUpload
      maxFiles={10}
      maxSize={100 * 1024 * 1024}
      className='w-full max-w-md'
      value={files}
      onValueChange={setFiles}
      onUpload={onUpload}
      multiple
    >
      <FileUploadDropzone>
        <div className='flex flex-col items-center gap-1 text-center'>
          <div className='flex items-center justify-center rounded-full border p-2.5'>
            <UploadIcon className='size-6 text-muted-foreground' />
          </div>
          <p className='font-medium text-sm'>Drag & drop files here</p>
          <p className='text-muted-foreground text-xs'>Or click to browse (up to 100 MiB)</p>
        </div>
        <FileUploadTrigger asChild>
          <Button variant='outline' size='sm' className='mt-2 w-fit'>
            Browse files
          </Button>
        </FileUploadTrigger>
      </FileUploadDropzone>
      <FileUploadList>
        {files.map((file, index) => (
          <FileUploadItem key={index} value={file} className='flex-col'>
            <div className='flex w-full items-center gap-2'>
              <FileUploadItemPreview />
              <FileUploadItemMetadata />
              <FileUploadItemDelete asChild>
                <Button variant='ghost' size='icon' className='size-7'>
                  <XIcon />
                </Button>
              </FileUploadItemDelete>
            </div>
            <FileUploadItemProgress />
          </FileUploadItem>
        ))}
      </FileUploadList>
    </FileUpload>
  )
}

export function Files({className, ...props}: ComponentProps<'div'>) {
  const [root, setRoot] = useLocalStorage('root', '')
  const [newDirs, setNewDirs] = useState<string[]>([])

  const auth = useAuth()

  const fetcher: Fetcher<{roots: string[]; files: FileData[]}, string> = (url: string, init = {}) =>
    fetch(url, {...init, headers: auth.header})
      .then(res => res.json())
      .then(res => (res.data ? res.data : Promise.reject(res.error)))

  const {data, error, isLoading} = useSWR(`/api/files/${root}`, fetcher, {refreshInterval: 15000})

  if (isLoading)
    return (
      <div className={className} {...props}>
        <Loader2Icon />
      </div>
    )

  if (error) {
    return (
      <Alert>
        <CircleAlertIcon />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  const tree = buildTree(data!.files, newDirs)

  return (
    <div className={cn('flex flex-col w-full h-full', className)} {...props}>
      <div className='m-6 grow-1 flex flex-col gap-6'>
        <div className='flex flex-row flex-wrap gap-1 lg:gap-6'>
          <NavLinks />
          <Select defaultValue={root || ''} onValueChange={setRoot}>
            <SelectTrigger className='w-full lg:max-w-48'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Root</SelectLabel>
                {(data?.roots || []).map(root => (
                  <SelectItem key={root} value={root}>
                    {root}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        {!data?.files?.length && (
          <Empty className='grow-1'>
            <EmptyHeader>
              <EmptyMedia variant='icon'>
                <LayoutListIcon />
              </EmptyMedia>
              <EmptyTitle>No files here</EmptyTitle>
            </EmptyHeader>
          </Empty>
        )}
        <Card>
          <CardContent>
            <RootContext.Provider value={root}>
              <Node node={tree} />
            </RootContext.Provider>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
